import React from 'react';
import gql from 'graphql-tag';
import { Mutation } from 'react-apollo';
import { curry, get, keys, omit, pick, set, uniqueId } from 'lodash';

import { addItem } from '../utils';
import { query, NAME as REQUEST_LINES_QUERY } from '../withCaseRequestLines';
import { emptyRequestLine, RequestLineFieldsFragment } from '../constants';

const NAME = 'compositions_CaseRequestPanel_CreateRequestLines';

const mutation = gql`
  mutation ${NAME} ($caseId: ID!, $requestLine: CaseLineAttrs!) {
    createCaseRequestLine(
      input: { caseId: $caseId, requestLine: $requestLine }
    ) {
      requestLine { ...RequestLineFields }
      agreementLine {
        agreed
        agreementLine { ...RequestLineFields }
        requestLineId
      }
    }
  }
  ${RequestLineFieldsFragment}
`;

const buildMutationChild = curry((WrappedComponent, componentProps, mutate) => {
  const createRequestedLine = ({ caseId, requestLine }) => {
    if (caseId) {
      const tempId = uniqueId('temp_');

      mutate({
        variables: {
          caseId,
          requestLine: pick(
            omit(requestLine, [
              // these values might be present in the requestLine, but are invalid
              // for the CaseLineAttrs type
              'id',
              'asset',
              'isReadOnly',
              'nonPreferredSelections',
              '__typename',
            ]),
            keys(emptyRequestLine).concat('assetId'),
          ),
        },
        optimisticResponse: {
          createCaseRequestLine: {
            __typename: 'CaseRequestLinePayload',
            agreementLine: {
              __typename: 'CaseAgreementLine',
              agreed: false,
              agreementLine: {
                ...emptyRequestLine,
                ...requestLine,
                __typename: 'CaseRequestLine',
                id: tempId,
              },
              requestLineId: tempId,
            },
            requestLine: {
              ...emptyRequestLine,
              ...requestLine,
              __typename: 'CaseRequestLine',
              id: tempId,
            },
          },
        },
        update: (store, { data }) => {
          const variables = { caseNumber: componentProps.caseNumber };
          const cache = store.readQuery({ query, variables });
          const savedLine = get(data, 'createCaseRequestLine.requestLine');

          const line = { ...emptyRequestLine, ...requestLine, ...savedLine };
          const newLines = addItem(get(cache, 'case.requestLines', []), line);
          const newCacheData = set(cache, 'case.requestLines', newLines);

          store.writeQuery({ query, data: newCacheData, variables });
        },
      });
    }
  };

  return (
    <WrappedComponent
      {...componentProps}
      createRequestedLine={createRequestedLine}
    />
  );
});

const withCaseCreateRequestLines = WrappedComponent => componentProps => (
  <Mutation mutation={mutation} refetchQueries={[REQUEST_LINES_QUERY]}>
    {buildMutationChild(WrappedComponent, componentProps)}
  </Mutation>
);

export default withCaseCreateRequestLines;



// WEBPACK FOOTER //
// ./src/compositions/CaseRequestsPanel/CaseRequestsPanelContext/withRequestLinesActions/withCaseCreateRequestLines.js